import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HubIcon from '@mui/icons-material/Hub';
import BookmarksIcon from '@mui/icons-material/Bookmarks';

const statusChip = (status) => {
  const normalized = (status || '').toUpperCase();
  if (normalized === 'ACTIVE') {
    return { label: 'Activo', color: 'var(--wb-success)', bg: 'var(--wb-x-bg-4ade8029, rgba(74,222,128,0.16))' };
  }
  if (normalized === 'SUPERSEDED') {
    return { label: 'Reemplazado', color: 'var(--wb-muted)', bg: 'var(--wb-border-16)' };
  }
  if (normalized === 'DISABLED') {
    return { label: 'Deshabilitado', color: 'var(--wb-danger)', bg: 'var(--wb-danger-16)' };
  }
  return { label: status || 'Desconocido', color: 'var(--wb-muted)', bg: 'var(--wb-border-16)' };
};

const AgentsTable = ({ agents, isLoading, onDelete, onViewDetails, onEdit, onViewKnowledgeBase, onReactivate, reactivatingId }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: 'var(--wb-bg-panel)',
        borderRadius: 3,
        border: '1px solid var(--wb-border)',
        p: { xs: 1.5, md: 2.5 }
      }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={36} />
        </Box>
      ) : !agents || agents.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            borderRadius: 2,
            background: 'var(--wb-bg-overlay-60)',
            border: '1px dashed var(--wb-border-20)'
          }}
        >
          <SmartToyIcon sx={{ color: 'var(--wb-muted-70)' }} />
          <Typography sx={{ color: 'var(--wb-muted-70)', mt: 1.5 }}>
            Aún no has creado agentes vinculados a tus espacios de análisis.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2.5}>
          {agents.map((agent, index) => {
            const chip = statusChip(agent.agentStatus);
            const hasKnowledgeBase = agent.knowledgeBases && agent.knowledgeBases.length > 0;
            const isActive = (agent.agentStatus || '').toUpperCase() === 'ACTIVE';

            return (
              <Box
                key={agent.agentId || agent.agentName || `agent-${index}`}
                sx={{
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, var(--wb-x-bg-2563eb14, rgba(37,99,235,0.08)), var(--wb-x-bg-6d28d90f, rgba(109,40,217,0.06)))',
                  border: '1px solid var(--wb-border-10)',
                  p: { xs: 2, md: 2.5 }
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        bgcolor: 'var(--wb-x-bg-1d4ed8, #1d4ed8)',
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SmartToyIcon sx={{ color: 'var(--wb-x-text-bfdbfe, #bfdbfe)' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.75 }}>
                        <Typography
                          variant="h6"
                          sx={{ color: 'var(--wb-text-title)', fontWeight: 700, fontSize: '1.05rem' }}
                        >
                          {agent.agentName || 'Agente sin nombre'}
                        </Typography>
                        <Chip
                          size="small"
                          label={chip.label}
                          sx={{
                            bgcolor: chip.bg,
                            color: chip.color,
                            fontWeight: 600
                          }}
                        />
                      </Stack>

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        divider={
                          <Box
                            component="span"
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: 'var(--wb-x-bg-94a3b866, rgba(148,163,184,0.4))',
                              alignSelf: 'center'
                            }}
                          />
                        }
                        sx={{ color: 'var(--wb-muted-85)' }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <HubIcon sx={{ fontSize: 18, color: 'var(--wb-x-text-a78bfa, #a78bfa)' }} />
                          <Typography variant="body2">
                            Modelo: {agent.semanticModelName || agent.semanticModelId || '—'}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BookmarksIcon sx={{ fontSize: 18, color: 'var(--wb-accent)' }} />
                          <Typography variant="body2">
                            Tools: {agent.enabledTools && agent.enabledTools.length > 0 ? agent.enabledTools.join(', ') : '—'}
                          </Typography>
                        </Stack>
                        {hasKnowledgeBase && onViewKnowledgeBase ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="KB asignada"
                            onClick={() => onViewKnowledgeBase(agent)}
                            sx={{
                              color: 'var(--wb-x-text-a5f3fc, #a5f3fc)',
                              borderColor: 'var(--wb-x-border-a5f3fc66, rgba(165,243,252,0.4))',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'var(--wb-x-bg-a5f3fc1a, rgba(165,243,252,0.1))' }
                            }}
                          />
                        ) : (
                          <Typography variant="body2">KB: Sin base de conocimiento</Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    {onReactivate && !isActive && (
                      <Tooltip title="Reactivar (reemplaza al agente activo de este modelo)">
                        <span>
                          <IconButton
                            onClick={() => onReactivate(agent)}
                            disabled={reactivatingId === agent.agentId}
                            sx={{
                              color: 'var(--wb-success)',
                              border: '1px solid var(--wb-x-border-4ade8066, rgba(74,222,128,0.4))',
                              '&:hover': { bgcolor: 'var(--wb-x-bg-4ade801a, rgba(74,222,128,0.1))' }
                            }}
                          >
                            {reactivatingId === agent.agentId
                              ? <CircularProgress size={18} />
                              : <RestartAltIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {onEdit && (
                      <Tooltip title={isActive ? 'Editar agente' : 'Solo se pueden editar agentes activos'}>
                        <span>
                          <IconButton
                            onClick={() => onEdit(agent)}
                            disabled={!isActive}
                            sx={{
                              color: 'var(--wb-x-text-a78bfa, #a78bfa)',
                              border: '1px solid var(--wb-x-border-a78bfa66, rgba(167,139,250,0.4))',
                              '&:hover': {
                                bgcolor: 'var(--wb-x-bg-a78bfa1a, rgba(167,139,250,0.1))'
                              }
                            }}
                          >
                            <EditOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {onViewDetails && (
                      <Tooltip title="Ver detalles">
                        <span>
                          <IconButton
                            onClick={() => onViewDetails(agent)}
                            sx={{
                              color: 'var(--wb-blue)',
                              border: '1px solid var(--wb-blue-40)',
                              '&:hover': {
                                bgcolor: 'var(--wb-x-bg-60a5fa1a, rgba(96,165,250,0.1))'
                              }
                            }}
                          >
                            <InfoOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {onDelete && (
                      <Tooltip title="Eliminar agente">
                        <span>
                          <IconButton
                            onClick={() => onDelete(agent)}
                            sx={{
                              color: 'var(--wb-danger)',
                              border: '1px solid var(--wb-x-border-f8717166, rgba(248,113,113,0.4))',
                              '&:hover': {
                                bgcolor: 'var(--wb-x-bg-f871711a, rgba(248,113,113,0.1))'
                              }
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

AgentsTable.propTypes = {
  agents: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  onDelete: PropTypes.func,
  onViewDetails: PropTypes.func,
  onEdit: PropTypes.func,
  onViewKnowledgeBase: PropTypes.func,
  onReactivate: PropTypes.func,
  reactivatingId: PropTypes.string
};

AgentsTable.defaultProps = {
  agents: [],
  isLoading: false,
  onDelete: null,
  onViewDetails: null,
  onEdit: null,
  onViewKnowledgeBase: null,
  onReactivate: null,
  reactivatingId: null
};

export default AgentsTable;
